"use client";

import { cn } from "@/lib/utils";
import { Mail, Calendar, CheckCircle, XCircle, Edit2, LogOut } from "lucide-react";
import { logoutAndRedirect } from "@/lib/auth-client";

interface ProfileCardProps {
  user: {
    id: string;
    name: string;
    email: string;
    emailVerified?: boolean;
    image?: string | null;
    createdAt?: string | Date;
  };
  className?: string;
}

export function ProfileCard({ user, className }: ProfileCardProps) {
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className={cn("card overflow-hidden animate-fade-in", className)}>
      {/* Header with gradient and liquid effect */}
      <div className="h-32 bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" />
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/20 blur-3xl animate-pulse" />
        <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-white/20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Avatar */}
      <div className="px-6 -mt-16 relative">
        <div className="relative inline-block">
          <div className="h-32 w-32 rounded-3xl border-4 border-white bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-4xl font-bold shadow-2xl shadow-primary-500/60">
            {user.image ? (
              <img
                src={user.image}
                alt={user.name}
                className="h-full w-full rounded-3xl object-cover"
              />
            ) : (
              getInitials(user.name)
            )}
          </div>
          <button className="absolute bottom-1 right-1 p-2.5 rounded-2xl bg-white border-2 border-white text-slate-600 hover:text-primary-600 shadow-lg hover:shadow-xl active:scale-95 transition-all duration-200">
            <Edit2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pt-4">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900">{user.name}</h3>
            <p className="text-sm font-semibold text-slate-500 mt-1">Orthoptiste</p>
          </div>
        </div>

        {/* Info fields */}
        <div className="space-y-3">
          <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 shadow-sm">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Email</p>
              <p className="text-sm font-semibold text-slate-900 truncate mt-0.5">
                {user.email}
              </p>
            </div>
            {user.emailVerified !== undefined && (
              <span
                className={cn(
                  user.emailVerified ? "badge-success" : "badge-warning"
                )}
              >
                {user.emailVerified ? (
                  <>
                    <CheckCircle className="h-3.5 w-3.5" />
                    Vérifié
                  </>
                ) : (
                  <>
                    <XCircle className="h-3.5 w-3.5" />
                    Non vérifié
                  </>
                )}
              </span>
            )}
          </div>

          {user.createdAt && (
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-secondary-100 to-secondary-200 text-secondary-700 shadow-sm">
                <Calendar className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Membre depuis</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                  {formatDate(user.createdAt)}
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => void logoutAndRedirect()}
            className="mt-4 w-full flex items-center justify-center gap-2 p-4 rounded-2xl border-2 border-error-200 bg-white/80 text-error-600 hover:bg-error-50 hover:border-error-300 font-semibold transition-all duration-200 active:scale-[0.98]"
          >
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}
